export const medicalTests = {
    CHLAMYDIA_ANAL_TAAN: "chlamydia_anal_taan",
    CHLAMYDIA_ORAL_TAAN: "chlamydia_oral_taan",
    CHLAMYDIA_URINARY_TAAN: "chlamydia_urinary_taan",
    GONORRHEA_ANAL_TAAN: "gonorrhea_anal_taan",
    GONORRHEA_ORAL_TAAN: "gonorrhea_oral_taan",
    GONORRHEA_URINARY_TAAN: "gonorrhea_urinary_taan",
    HEPATITE_B: "hepatite_b",
    HEPATITE_C_ANTI_VHC: "hepatite_c_anti_vhc",
    HEPATITE_C_ARN_VHC: "hepatite_c_arn_vhc",
    HIV: "hiv",
    SYPHILIS: "syphilis",
} as const;

export type MedicalTestName = typeof medicalTests[keyof typeof medicalTests];

export type MedicalTest = {
    medicalTest: {
        name: string;
    };
};

type PriceListItem = {
    coveredTests: readonly MedicalTestName[];
    cost: number;
};

export type PriceList = Record<string, PriceListItem>;

// Order matters, treat the items in descending order of number of covered tests
// Otherwise the price calculation will not pick the least expensive option
export const priceList = {
    // test bundles
    complete_3_sites: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 255,
    },
    chlamydia_gonorrhea_3_sites: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
        ],
        cost: 180,
    },
    complete_2_sites_anal_oral: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 225,
    },
    complete_2_sites_anal_urinary: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 225,
    },
    complete_2_sites_oral_urinary: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 225,
    },
    chlamydia_gonorrhea_2_sites_anal_oral: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
        ],
        cost: 150,
    },
    chlamydia_gonorrhea_2_sites_anal_urinary: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
        ],
        cost: 150,
    },
    chlamydia_gonorrhea_2_sites_oral_urinary: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
        ],
        cost: 150,
    },
    complete_1_site_anal: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 188,
    },
    complete_1_site_oral: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 188,
    },
    complete_1_site_urinary: {
        coveredTests: [
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
            medicalTests.HIV,
            medicalTests.SYPHILIS,
        ],
        cost: 188,
    },
    chlamydia_gonorrhea_1_site_anal: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ANAL_TAAN,
            medicalTests.GONORRHEA_ANAL_TAAN,
        ],
        cost: 113,
    },
    chlamydia_gonorrhea_1_site_oral: {
        coveredTests: [
            medicalTests.CHLAMYDIA_ORAL_TAAN,
            medicalTests.GONORRHEA_ORAL_TAAN,
        ],
        cost: 113,
    },
    chlamydia_gonorrhea_1_site_urinary: {
        coveredTests: [
            medicalTests.CHLAMYDIA_URINARY_TAAN,
            medicalTests.GONORRHEA_URINARY_TAAN,
        ],
        cost: 113,
    },
    hiv_syphilis: {
        coveredTests: [medicalTests.HIV, medicalTests.SYPHILIS],
        cost: 79,
    },
    //single tests
    hepatite_b: {
        coveredTests: [medicalTests.HEPATITE_B],
        cost: 64,
    },
    hepatite_c_anti_vhc: {
        coveredTests: [medicalTests.HEPATITE_C_ANTI_VHC],
        cost: 76,
    },
    hepatite_c_arn_vhc: {
        coveredTests: [medicalTests.HEPATITE_C_ARN_VHC],
        cost: 148,
    },
    hiv: {
        coveredTests: [medicalTests.HIV],
        cost: 69,
    },
    syphilis: {
        coveredTests: [medicalTests.SYPHILIS],
        cost: 31,
    },
} as PriceList;

export class PrivateLabCalculator {
    private tests: string[];

    public constructor(
        private priceList: PriceList,
        medicalTests: MedicalTest[]
    ) {
        this.tests = medicalTests.map((test) => test.medicalTest.name);
    }

    public total(): number | undefined {
        let total = 0;
        const remainingTests = new Set(this.tests);

        const items = Object.keys(this.priceList);

        for (const item of items) {
            if (this.hasPriceListItem(remainingTests, item)) {
                total += this.priceList[item].cost;

                this.priceList[item].coveredTests.forEach((test) =>
                    remainingTests.delete(test)
                );
            }
        }

        if (remainingTests.size > 0) {
            return undefined;
        }

        return total;
    }

    private hasPriceListItem(tests: Set<string>, item: string) {
        return this.priceList[item].coveredTests.every((test) =>
            tests.has(test)
        );
    }
}
